import type { KpiAxisConfiguration, KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { extractKpiAxisBoundsFromKpis } from 'components/pages/PackArchitecture/packArchitectureChartKpiAxis.helpers';
import type { PpaChartBinConfig, PpaChartKpi } from 'components/pages/PackArchitecture/pricePackArchitecture.types';
import { DEFAULT_UNIFORM_BIN_COUNT } from 'constants/ppa';
import { colorConstants } from 'config/colorConstants';
import { formatPpaMetricValue } from 'utils/ppaHelpers';

const Y_AXIS_MIN_KPI_IDS = ['y_axis_min'];
const Y_AXIS_MAX_KPI_IDS = ['y_axis_max'];
const VALUE_SALES_MIN_KPI_ID = 'value_sales_min';
const VALUE_SALES_MAX_KPI_ID = 'value_sales_max';
const HEATMAP_MIN_COLOR_KPI_ID = 'heatmap_min_color';
const HEATMAP_MAX_COLOR_KPI_ID = 'heatmap_max_color';
const DEFAULT_HEATMAP_GRADIENT_START = colorConstants.whitespaceHeatmapGradientStart;
const DEFAULT_HEATMAP_GRADIENT_END = colorConstants.whitespaceHeatmapGradientEnd;
const DEFAULT_VALUE_SALES_LABEL = 'Value Sales';
const DEFAULT_VALUE_SALES_PREFIX = '$';

export type WhitespaceValueSalesLegendData = {
    label: string;
    minLabel: string;
    maxLabel: string;
    gradientStartColor: string;
    gradientEndColor: string;
};

type WhitespaceGridColumnGroup = {
    kpis?: PpaChartKpi[];
    bin_config?: PpaChartBinConfig;
};

export type WhitespaceGridKpiAxisResponse = {
    data?: WhitespaceGridColumnGroup[];
    kpis?: PpaChartKpi[];
    bin_config?: PpaChartBinConfig;
};

const resolveWhitespaceGridAxisMeta = (
    response: WhitespaceGridKpiAxisResponse | null | undefined,
): { kpis?: PpaChartKpi[]; bin_config?: PpaChartBinConfig } => {
    if (!response) return {};

    if (response.kpis?.length || response.bin_config) {
        return { kpis: response.kpis, bin_config: response.bin_config };
    }

    for (const group of response.data ?? []) {
        if (group.kpis?.length || group.bin_config) {
            return { kpis: group.kpis, bin_config: group.bin_config };
        }
    }

    return {};
};

const normalizeKpiId = (kpiId?: string) => (kpiId ?? '').trim().toLowerCase();

const buildRootKpiLookup = (kpis: PpaChartKpi[]) =>
    new Map(kpis.map((kpi) => [normalizeKpiId(kpi.kpi_id), kpi]));

const resolveKpiLabel = (primary: PpaChartKpi, secondary: PpaChartKpi) =>
    primary.display_label?.trim() ||
    secondary.display_label?.trim() ||
    primary.kpi_name?.trim() ||
    secondary.kpi_name?.trim() ||
    DEFAULT_VALUE_SALES_LABEL;

const resolveNumericKpiValue = (kpi: PpaChartKpi): string | number | undefined => {
    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        return kpi.raw_value;
    }

    const { value } = kpi;
    return typeof value === 'string' || typeof value === 'number' ? value : undefined;
};

const formatHeatmapBoundLabel = (kpi: PpaChartKpi, fallbackPrefix = DEFAULT_VALUE_SALES_PREFIX) => {
    const prefix = kpi.valuePrefix ?? fallbackPrefix;
    const formatted = formatPpaMetricValue(resolveNumericKpiValue(kpi));

    if (formatted === '-') return formatted;
    return /^[^0-9+-]/.test(formatted) ? formatted : `${prefix}${formatted}`;
};

const resolveHeatmapColor = (kpi: PpaChartKpi | undefined, fallback: string) => {
    const color = typeof kpi?.value === 'string' ? kpi.value.trim() : '';
    return color || fallback;
};

/** Reads Value Sales heatmap legend bounds from whitespace grid API root `kpis`. */
export const extractWhitespaceValueSalesLegendFromGridData = (
    response: WhitespaceGridKpiAxisResponse | null | undefined,
): WhitespaceValueSalesLegendData | null => {
    const rootKpis = response?.kpis;
    if (!rootKpis?.length) return null;

    const kpiLookup = buildRootKpiLookup(rootKpis);
    const minKpi = kpiLookup.get(VALUE_SALES_MIN_KPI_ID);
    const maxKpi = kpiLookup.get(VALUE_SALES_MAX_KPI_ID);
    if (!minKpi || !maxKpi) return null;

    return {
        label: resolveKpiLabel(minKpi, maxKpi),
        minLabel: formatHeatmapBoundLabel(minKpi),
        maxLabel: formatHeatmapBoundLabel(maxKpi),
        gradientStartColor: resolveHeatmapColor(
            kpiLookup.get(HEATMAP_MIN_COLOR_KPI_ID),
            DEFAULT_HEATMAP_GRADIENT_START,
        ),
        gradientEndColor: resolveHeatmapColor(
            kpiLookup.get(HEATMAP_MAX_COLOR_KPI_ID),
            DEFAULT_HEATMAP_GRADIENT_END,
        ),
    };
};

/** Reads Y-axis slider bounds from whitespace grid API `kpis` (`y_axis_min` / `y_axis_max`). */
export const extractWhitespaceYKpiBoundsFromGridData = (
    response: WhitespaceGridKpiAxisResponse | null | undefined,
): KpiDataRange | undefined => {
    const { kpis } = resolveWhitespaceGridAxisMeta(response);
    return extractKpiAxisBoundsFromKpis(kpis, Y_AXIS_MIN_KPI_IDS, Y_AXIS_MAX_KPI_IDS);
};

/** Maps whitespace grid API metadata to the KPI axis configuration modal state. */
export const extractWhitespaceYKpiConfigFromGridData = (
    response: WhitespaceGridKpiAxisResponse | null | undefined,
    fallbackDataRange: KpiDataRange,
): KpiAxisConfiguration | undefined => {
    const { kpis, bin_config: binConfig } = resolveWhitespaceGridAxisMeta(response);
    const boundsFromKpis = extractKpiAxisBoundsFromKpis(kpis, Y_AXIS_MIN_KPI_IDS, Y_AXIS_MAX_KPI_IDS);

    if (!boundsFromKpis && !binConfig) {
        return undefined;
    }

    const min = boundsFromKpis?.defaultMin ?? fallbackDataRange.defaultMin;
    const max = boundsFromKpis?.defaultMax ?? fallbackDataRange.defaultMax;
    const binningMethod: KpiAxisConfiguration['binningMethod'] =
        binConfig?.method === 'custom' ? 'custom' : 'uniform';
    const binCount =
        typeof binConfig?.bin_count === 'number' && binConfig.bin_count > 0
            ? binConfig.bin_count
            : DEFAULT_UNIFORM_BIN_COUNT;
    const rawBreakpoints = binConfig?.breakpoints;
    const breakpoints = Array.isArray(rawBreakpoints)
        ? rawBreakpoints.filter((value): value is number => Number.isFinite(value))
        : [];

    return { min, max, binningMethod, binCount, breakpoints };
};
